import { useState } from "react";
import api, { formatApiError } from "../lib/api";
import { PageHeader } from "../components/PageHeader";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { toast } from "sonner";
import { Loader2, PackageMinus } from "lucide-react";
import RecipeMapPanel from "../components/sales/RecipeMapPanel";
import StockMovementsPanel from "../components/sales/StockMovementsPanel";
import PendingSalesPanel from "../components/sales/PendingSalesPanel";
import ReconciliationPanel from "../components/sales/ReconciliationPanel";

const iso = (d) => d.toISOString().slice(0, 10);

export default function Sales() {
  const now = new Date();
  const [section, setSection] = useState("vendite");
  const [from, setFrom] = useState(iso(new Date(now.getFullYear(), now.getMonth(), 1)));
  const [to, setTo] = useState(iso(now));
  const [busy, setBusy] = useState(false);
  const [refresh, setRefresh] = useState(0);
  const params = { from_date: from, to_date: to };

  const applyDeductions = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/sales/apply-deductions", null, { params });
      toast.success(`Scaricati ${data.deducted} piatti · ${data.unmatched} senza ricetta`);
      if (data.low_stock?.length) toast.warning(`Sotto soglia dopo lo scarico: ${data.low_stock.join(", ")}`);
      setRefresh((r) => r + 1);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div data-testid="sales-page">
      <PageHeader title="Vendite" subtitle="Abbinamento piatti ↔ ricette, scarico magazzino, uscite in sospeso e riconciliazione POS" testid="sales-header">
        {section === "vendite" && (
          <Button className="rounded-full" onClick={applyDeductions} disabled={busy} data-testid="apply-deductions-button">
            {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <PackageMinus className="w-4 h-4 mr-2" />} Scarica magazzino (periodo)
          </Button>
        )}
      </PageHeader>

      <Tabs value={section} onValueChange={setSection} className="mb-6">
        <TabsList className="rounded-full h-auto" data-testid="sales-sections">
          <TabsTrigger value="vendite" className="rounded-full" data-testid="section-vendite">Vendite</TabsTrigger>
          <TabsTrigger value="riconciliazione" className="rounded-full" data-testid="section-riconciliazione">Riconciliazione POS</TabsTrigger>
        </TabsList>
      </Tabs>

      {section === "vendite" ? (
        <>
          <Card className="p-4 rounded-xl mb-6" data-testid="sales-period">
            <div className="flex flex-wrap items-end gap-3">
              <div className="space-y-1"><Label className="text-xs">Dal</Label><Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="h-9" data-testid="sales-from" /></div>
              <div className="space-y-1"><Label className="text-xs">Al</Label><Input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="h-9" data-testid="sales-to" /></div>
              <p className="text-xs text-muted-foreground">Il venduto arriva da Import Cassa (PDF "Venduto Prodotti"), dall'ingest EasyEvolver e dalle uscite in sospeso.</p>
            </div>
          </Card>

          <Tabs defaultValue="map">
            <TabsList className="rounded-full mb-4 flex-wrap h-auto">
              <TabsTrigger value="map" className="rounded-full" data-testid="tab-map">Abbinamenti piatti</TabsTrigger>
              <TabsTrigger value="movements" className="rounded-full" data-testid="tab-movements">Scarichi magazzino</TabsTrigger>
              <TabsTrigger value="pending" className="rounded-full" data-testid="tab-pending">Uscite in sospeso</TabsTrigger>
            </TabsList>
            <TabsContent value="map"><RecipeMapPanel params={params} refresh={refresh} /></TabsContent>
            <TabsContent value="movements"><StockMovementsPanel params={params} refresh={refresh} /></TabsContent>
            <TabsContent value="pending"><PendingSalesPanel onChange={() => setRefresh((r) => r + 1)} /></TabsContent>
          </Tabs>
        </>
      ) : (
        <ReconciliationPanel />
      )}
    </div>
  );
}
